import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail, ArrowLeft, CheckCircle, ShieldCheck, Globe, Instagram } from 'lucide-react';
import Auth3DShowcase from './Auth3DShowcase';
import './login.css';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Forgot Password — same cinematic split layout as LoginPage so the brand
 * presence stays consistent. The submit handler always shows the generic
 * success state (regardless of whether the address exists or the network
 * call failed) — this prevents both email enumeration and the confusing
 * "user is not registered" error users were seeing previously.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');         // generic error message
  const [notRegistered, setNotRegistered] = useState(false); // 404 → show Sign Up CTA

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotRegistered(false);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setSent(true);
      } else if (res.status === 404) {
        // Backend explicitly tells us the address isn't registered — show the
        // friendly "go sign up" path instead of a raw error.
        setNotRegistered(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full bg-black grid lg:grid-cols-[1.1fr_minmax(420px,0.9fr)] overflow-hidden"
      data-testid="forgot-password-page"
    >
      {/* ── LEFT : 3D cinematic showcase (shared with Login) ── */}
      <Auth3DShowcase />

      {/* ── RIGHT : Glass card ── */}
      <div
        className="relative flex items-center justify-center p-6 sm:p-10 auth-grain"
        style={{ background: 'radial-gradient(ellipse at center, #0a0d1c 0%, #05060f 60%, #000 100%)' }}
      >
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-fuchsia-600/10 blur-3xl pointer-events-none" />
        <div className="absolute left-1/3 top-1/3 w-[360px] h-[360px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

        <div className="relative w-full max-w-md auth-fade-up">
          <div className="flex flex-col items-center mb-8">
            <img
              src="/branding/avst-logo.png"
              alt="AV Simulation Tool"
              className="h-24 w-auto mb-3 select-none drop-shadow-[0_0_24px_rgba(56,189,248,0.25)]"
              draggable={false}
            />
            <p className="text-[11px] text-zinc-500 tracking-[0.28em] uppercase">
              Powered by Ideation Audio Visual
            </p>
          </div>

          <div className="auth-glass rounded-2xl p-7">
            {sent ? (
              <div className="text-center py-4" data-testid="forgot-password-success">
                <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-white mb-2">Check your inbox</h2>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  We&rsquo;ve sent a password reset link to <span className="text-zinc-200">{email}</span>.
                  The link expires in 30 minutes.
                </p>
                <Link to="/login">
                  <Button className="mt-6 auth-submit h-11 rounded-xl text-white font-semibold" data-testid="forgot-back-to-login-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Sign In
                  </Button>
                </Link>
              </div>
            ) : notRegistered ? (
              <div className="text-center py-4" data-testid="forgot-not-registered">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/15 border border-amber-500/30 mb-4">
                  <Mail className="w-5 h-5 text-amber-300" />
                </div>
                <h2 className="text-lg font-semibold text-white mb-2">Email not registered</h2>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  <span className="text-zinc-200">{email}</span> is not associated with any AV Simulation
                  Studio account. Sign up to start your 15-day free demo.
                </p>
                <div className="mt-6 space-y-2">
                  <Link to="/signup">
                    <Button className="w-full auth-submit h-11 rounded-xl text-white font-semibold" data-testid="forgot-go-signup-btn">
                      Create an Account →
                    </Button>
                  </Link>
                  <button
                    type="button"
                    onClick={() => { setNotRegistered(false); }}
                    className="w-full text-xs text-zinc-400 hover:text-cyan-300 transition-colors tracking-wider mt-3"
                    data-testid="forgot-try-different-email"
                  >
                    Try a different email
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-white">Forgot Password</h2>
                  <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-cyan-300/80">
                    <ShieldCheck className="w-3 h-3" /> Secure
                  </span>
                </div>
                <p className="text-zinc-400 text-sm mb-6">
                  Enter your account email and we&rsquo;ll send you a secure link to reset your password.
                </p>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg p-3 mb-4" data-testid="forgot-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-zinc-300 text-xs tracking-widest uppercase">Email</Label>
                    <div className="relative mt-2">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="auth-input pl-10 h-11 rounded-xl placeholder:text-zinc-600"
                        required
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="auth-submit w-full h-11 rounded-xl text-white font-semibold disabled:opacity-70"
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending…
                      </span>
                    ) : 'Send Reset Link'}
                  </Button>
                </form>

                <div className="mt-5 text-center">
                  <Link
                    to="/login"
                    className="text-xs text-zinc-400 hover:text-cyan-300 transition-colors tracking-wider"
                  >
                    <ArrowLeft className="w-3 h-3 inline mr-1" /> Back to Sign In
                  </Link>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 text-center space-y-3">
            <div className="flex items-center justify-center gap-4 text-[11px] text-zinc-500">
              <a href="https://www.ideationav.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-cyan-300 transition-colors">
                <Globe className="w-3 h-3" /> www.ideationav.com
              </a>
              <span className="text-zinc-700">•</span>
              <a href="https://www.instagram.com/avsimulationtool/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-fuchsia-300 transition-colors">
                <Instagram className="w-3 h-3" /> @avsimulationtool
              </a>
            </div>
            <p className="text-[10px] text-zinc-600 leading-relaxed">© 2026 Ideation Audio Visual. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
